'use client';
import { useCallback, useEffect } from 'react';

import axios from 'axios';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';

import {
  useClearDispatch,
  DISCONNECTION_REASONS,
  DISCONNECTION_REASON_STORAGE_KEY,
  useAuthState,
  useSetTokenExpirationDate,
} from '@contexts/auth/AuthContext';
import { callService } from '@api/remote-call';
import {
  getExpirationDate,
  saveTokenInfo,
} from '@components/views/login/utils';
import { REFRESH_TOKEN_KEY } from '@components/views/login/constants';
import { useCookies, useSetCookies } from '@contexts/cookies';
import useGetasPath from '@hooks/useGetasPath';
import {
  getSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';

import { getLoginSessionExpirationDate } from './tokens';
import { useTabManager } from './useTabManager';

let logoutTime: NodeJS.Timeout;
let tokenExpiresTime: NodeJS.Timeout;

const AuthTokenExpiration = () => {
  const clearUserInformation = useClearDispatch();
  const { tokenExpirationDate } = useAuthState();
  const tokenExpirationDispatch = useSetTokenExpirationDate();
  const cookies = useCookies();
  const dispatch = useSetCookies();
  const { refresh } = useRouter();
  const asPath = useGetasPath();
  const { tabId } = useTabManager();

  const handleClearUserInfo = useCallback(() => {
    clearUserInformation();

    setSafeLocalStorageItem(
      DISCONNECTION_REASON_STORAGE_KEY,
      DISCONNECTION_REASONS.SESSION_EXPIRATION
    );
  }, [clearUserInformation]);

  const handleTokenExpiration = useCallback(async () => {
    const activeTabsData = getSafeLocalStorageItem('activeTabs');
    const storedTabs = activeTabsData ? JSON.parse(activeTabsData) : [];

    if (
      storedTabs?.length === 0 ||
      (storedTabs?.length > 0 &&
        storedTabs.sort((a, b) => a.id - b.id)[storedTabs.length - 1].id ===
          tabId)
    ) {
      const refreshToken = getSafeLocalStorageItem(REFRESH_TOKEN_KEY);

      const userInfoData = getSafeLocalStorageItem('userInfo');
      const userInfo = userInfoData ? JSON.parse(userInfoData) : {};
      const userEmail = userInfo.userName;
      if (!userInfo?.userName) return null;

      if (!refreshToken) {
        handleClearUserInfo();
        window.location.reload();
        return;
      }

      try {
        const tokenData = await callService(
          {
            service: 'AuthService',
            method: 'tokenRefresh',
            version: 'v3',
          },
          { body: { refreshToken, email: userEmail ?? '' } }
        );

        if (!tokenData?.token) {
          handleClearUserInfo();
          window.location.reload();
          return;
        }

        saveTokenInfo(tokenData);
        dispatch({
          type: 'SET_COOKIE',
          key: 'token',
          value: `${tokenData.tokenType} ${tokenData.token}`,
        });

        try {
          await axios.post('/api/token', {
            tokenType: tokenData.tokenType,
            token: tokenData.token,
            maxAge: tokenData.expires,
          });

          setSafeLocalStorageItem(
            'token',
            `${tokenData.tokenType} ${tokenData.token}`
          );
          refresh();
        } catch (error) {
          Sentry.captureMessage(
            `AuthTokenExpiration.handleTokenExpiration Error overriding cookie : ${JSON.stringify(
              error
            )}`
          );
        }

        const tokenExpirationDate = getExpirationDate(tokenData.expires);
        tokenExpirationDispatch(tokenExpirationDate);
      } catch (error) {
        handleClearUserInfo();
        Sentry.captureMessage(
          `AuthTokenExpiration.handleTokenExpiration Error : ${JSON.stringify(
            error
          )}`
        );
      }
    }
  }, [tokenExpirationDispatch, dispatch]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'tokenRefreshQueue') {
        handleTokenExpiration();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleTabActivity = () => {
      if (tokenExpirationDate) {
        const expirationTimeWithBuffer = tokenExpirationDate.getTime() - 5000;

        if (expirationTimeWithBuffer <= new Date().getTime()) {
          handleTokenExpiration();
        }
      }
    };

    window.addEventListener('focus', handleTabActivity);
    window.addEventListener('pageshow', handleTabActivity);

    return () => {
      window.removeEventListener('focus', handleTabActivity);
      window.removeEventListener('pageshow', handleTabActivity);
    };
  }, [tokenExpirationDate]);

  useEffect(() => {
    clearTimeout(tokenExpiresTime);
    if (tokenExpirationDate) {
      const timeoutDelay = tokenExpirationDate.getTime() - new Date().getTime();
      const delayedTokenExpirationTime = Math.max(0, timeoutDelay - 5000);

      tokenExpiresTime = setTimeout(async () => {
        handleTokenExpiration();
      }, delayedTokenExpirationTime);
    }
  }, [tokenExpirationDate, clearUserInformation]);

  useEffect(() => {
    const LLToken = getSafeLocalStorageItem('token');
    // if token in cookies are different than token in local storage, handle token expiration
    if (cookies?.token && LLToken && cookies.token !== LLToken) {
      handleTokenExpiration().then(() => {
        refresh();
      });
    }
  }, [cookies.token]);

  useEffect(() => {
    clearTimeout(logoutTime);
    const { loginSessionExpiration, loginSessionExpirationDate } =
      getLoginSessionExpirationDate();

    if (loginSessionExpiration) {
      logoutTime = setTimeout(() => {
        clearUserInformation();
        setSafeLocalStorageItem(
          DISCONNECTION_REASON_STORAGE_KEY,
          DISCONNECTION_REASONS.SESSION_EXPIRATION
        );
        window.location.href = asPath;
        window.location.reload();
      }, loginSessionExpirationDate.getTime() - new Date().getTime());
    }
  }, [clearUserInformation]);

  return null;
};

export default AuthTokenExpiration;
